/**
 * Panel de posicion de tesoreria.
 *
 * La cifra consolidada es el dato que el jefe mira primero: va en 60px,
 * tabular y con la fecha del ultimo extracto al lado (frescura del dato).
 * Por debajo, una tarjeta por empresa con sus cuentas.
 *
 * Nunca se inventan cifras en el cliente: todo importe viene calculado por el
 * backend (`api.posicion`). Aqui solo se formatea.
 */
import { Tarjeta, TarjetaCabecera, TarjetaTitulo, TarjetaContenido } from '@/components/ui/card';
import { formatearEuros, formatearFechaLarga, cn } from '@/lib/utils';
import type { Posicion, PosicionEmpresa, SaldoCuenta } from '@/lib/api';

interface PropsPosicionPanel {
  posicion: Posicion | null;
  cargando?: boolean;
  error?: string | null;
  /** Empresa desplegada; si es null se muestran todas plegadas al detalle. */
  empresaSeleccionada?: number | null;
  alSeleccionarEmpresa?: (empresaId: number | null) => void;
  className?: string;
}

export function PosicionPanel({
  posicion,
  cargando = false,
  error = null,
  empresaSeleccionada = null,
  alSeleccionarEmpresa,
  className,
}: PropsPosicionPanel) {
  if (error) {
    return (
      <Tarjeta className={cn('border-destructive', className)}>
        <TarjetaContenido>
          <p className="text-lg">No he podido leer la posicion.</p>
          <p className="mt-2 text-sm text-muted-foreground">{error}</p>
        </TarjetaContenido>
      </Tarjeta>
    );
  }

  if (cargando || !posicion) {
    return (
      <Tarjeta className={className}>
        <TarjetaContenido>
          <p className="text-lg text-muted-foreground">Consultando la posicion…</p>
        </TarjetaContenido>
      </Tarjeta>
    );
  }

  const algunaBajoUmbral = posicion.empresas.some((e) => e.bajo_umbral);

  return (
    <section className={cn('flex flex-col gap-5', className)} aria-label="Posición de tesoreria">
      <Tarjeta className={cn(algunaBajoUmbral && 'border-[hsl(var(--alerta))]')}>
        <TarjetaCabecera>
          <TarjetaTitulo>Posicion consolidada</TarjetaTitulo>
          <p className="text-sm text-muted-foreground">
            A {formatearFechaLarga(posicion.fecha)}
            {posicion.ultima_ingesta && ` · ultimo extracto del ${formatearFechaLarga(posicion.ultima_ingesta)}`}
          </p>
        </TarjetaCabecera>
        <TarjetaContenido>
          <p className="cifra text-4xl font-bold tabular-nums">
            {formatearEuros(posicion.saldo_consolidado)}
          </p>
          {algunaBajoUmbral && (
            <p className="mt-4 text-lg font-semibold text-[hsl(var(--alerta))]">
              Hay alguna cuenta por debajo de su saldo de seguridad.
            </p>
          )}
        </TarjetaContenido>
      </Tarjeta>

      <div className="grid gap-5 md:grid-cols-2">
        {posicion.empresas.map((empresa) => (
          <FilaEmpresa
            key={empresa.empresa_id}
            empresa={empresa}
            desplegada={empresaSeleccionada === empresa.empresa_id}
            alAlternar={() =>
              alSeleccionarEmpresa?.(
                empresaSeleccionada === empresa.empresa_id ? null : empresa.empresa_id,
              )
            }
          />
        ))}
      </div>
    </section>
  );
}

/** Tarjeta de una empresa; el encabezado entero es el objetivo tactil. */
function FilaEmpresa({
  empresa,
  desplegada,
  alAlternar,
}: {
  empresa: PosicionEmpresa;
  desplegada: boolean;
  alAlternar: () => void;
}) {
  return (
    <Tarjeta className={cn(empresa.bajo_umbral && 'border-[hsl(var(--alerta))]')}>
      <button
        type="button"
        onClick={alAlternar}
        aria-expanded={desplegada}
        className="flex min-h-tactil w-full items-center justify-between gap-5 rounded-lg p-6 text-left"
      >
        <span className="text-lg font-semibold">{empresa.nombre}</span>
        <span className="cifra text-2xl font-bold tabular-nums">
          {formatearEuros(empresa.saldo_total)}
        </span>
      </button>

      {desplegada && (
        <TarjetaContenido className="pt-0">
          <ul className="flex flex-col gap-4">
            {empresa.cuentas.map((cuenta) => (
              <FilaCuenta key={cuenta.cuenta_id} cuenta={cuenta} />
            ))}
          </ul>
        </TarjetaContenido>
      )}
    </Tarjeta>
  );
}

/** Linea de cuenta bancaria. El IBAN llega ya censurado desde el backend. */
function FilaCuenta({ cuenta }: { cuenta: SaldoCuenta }) {
  return (
    <li className="flex items-baseline justify-between gap-5 border-t-2 border-border pt-4">
      <span className="flex flex-col">
        <span className="text-base font-medium">{cuenta.alias}</span>
        <span className="text-sm text-muted-foreground">
          {cuenta.entidad} · {cuenta.iban_visible}
        </span>
      </span>
      <span
        className={cn(
          'cifra text-lg font-semibold tabular-nums',
          cuenta.bajo_umbral && 'text-[hsl(var(--alerta))]',
        )}
      >
        {formatearEuros(cuenta.saldo_actual)}
      </span>
    </li>
  );
}
